import React from 'react'

type UseLazyThumbnailOptions = {
  cacheKey: string
  getCached: () => string | null
  loadCached?: () => Promise<string | null>
  ensure: () => Promise<string>
  fallbackDelayMs?: number
  onError?: (err: unknown) => void
}

export function useLazyThumbnail({
  cacheKey,
  getCached,
  loadCached,
  ensure,
  fallbackDelayMs = 100,
  onError,
}: UseLazyThumbnailOptions): {
  src: string | null
  setSrc: React.Dispatch<React.SetStateAction<string | null>>
  wrapperRef: React.RefObject<HTMLDivElement | null>
} {
  const [src, setSrc] = React.useState<string | null>(() => getCached())
  const wrapperRef = React.useRef<HTMLDivElement | null>(null)
  const askedRef = React.useRef(false)

  React.useEffect(() => {
    let cancelled = false
    askedRef.current = false
    const cached = getCached()
    setSrc(cached)
    if (!cached && loadCached) {
      loadCached().then((url) => {
        if (!cancelled && url) setSrc(url)
      }).catch((err) => {
        if (!cancelled) onError?.(err)
      })
    }
    return () => {
      cancelled = true
    }
  }, [cacheKey, getCached, loadCached, onError])

  React.useEffect(() => {
    if (src) return
    const el = wrapperRef.current
    if (!el) return
    let io: IntersectionObserver | null = null
    let cancelled = false
    const ensureOnce = () => {
      if (askedRef.current) return
      askedRef.current = true
      ensure().then((url) => {
        if (!cancelled && url) setSrc(url)
      }).catch((err) => {
        if (!cancelled) onError?.(err)
      })
    }
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) { ensureOnce(); break }
        }
      }, { root: null, rootMargin: '100px', threshold: 0 })
      io.observe(el)
    } else {
      const timeout = setTimeout(ensureOnce, fallbackDelayMs)
      return () => {
        cancelled = true
        clearTimeout(timeout)
      }
    }
    return () => {
      cancelled = true
      io?.disconnect()
    }
  }, [ensure, fallbackDelayMs, onError, src])

  return { src, setSrc, wrapperRef }
}
